import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Home, ArrowLeft, ArrowRight, Images, Upload, AlertCircle, X, Navigation } from 'lucide-react';
import { RetrofitSurveyData, getRetrofitSurveyAddress } from '../../utils/retrofitSurveyStorage';
import PhotoCapture from '../PhotoCapture';
import PhotoGalleryModal from '../PhotoGalleryModal';
import type { PhotoData } from '../../services/CameraService';
import { useRetrofitPhotoUpload } from '../../hooks/useRetrofitPhotoUpload';
import { UseAutosaveReturn } from '../../hooks/useAutosave';
import { CompassMeasurer } from '../CompassMeasurer';
import PropertyFrontIconSrc from '../../assets/20250603_2041_House with \'F\' Logo_remix_01jwvmyq2qerz9qqh5a2008cqd Background Removed.png';
import DynamicFieldRenderer from '../DynamicForm/DynamicFieldRenderer';
import type { FieldSchema } from '../../types/formSchema';
import { deletePhotoFromSupabase } from '../../utils/supabaseStorage';

interface PropertyFrontFormProps {
  onNavigateNext: () => void;
  onNavigateBack: () => void;
  onBuildFormChange: (buildForm: string, exposedSide: 'left' | 'right' | 'both' | 'none' | '') => void;
  floorPlanId?: string;
  surveyData?: RetrofitSurveyData;
  surveyId?: string;
  onDataUpdate?: (updater: (prev: RetrofitSurveyData) => RetrofitSurveyData, sectionId?: string) => void;
  initialBuildForm: string;
  initialExposedSide: 'left' | 'right' | '';
  autosave?: UseAutosaveReturn;
  onSaveImmediately?: () => Promise<string>;
  onGalleryStateChange?: (isOpen: boolean) => void;
  customFields?: any[];  // Custom fields for this page
  hiddenFields?: string[];  // Hidden built-in fields
  hiddenSections?: string[];  // Hidden custom sections
  sectionOrder?: string[];  // Order of sections (built-in + custom)
  builtInFieldOrder?: string[];  // Order of built-in fields
}

// Enhanced PhotoField interface with progressive enhancement support
interface PhotoField {
  id: string;
  dataUrl: string;      // Compressed base64 (800x600, 75%) - always available for offline
  fileName?: string;    // Supabase storage path (e.g., "survey-123/property_front/photo-456.jpg")
  url?: string;         // Supabase CDN URL after successful upload
  uploading?: boolean;  // True while upload is in progress
  error?: string;       // Error message if upload failed
}

// For items in arrays of multiple photos, extending original PhotoData
interface DisplayablePhotoData extends PhotoData {
  displayId: string;    // Unique ID for React keys and removal by ID if needed
  dataUrl: string;      // Compressed base64 - always available for offline
  fileName?: string;    // Supabase storage path
  url?: string;         // Supabase CDN URL after upload
  uploading?: boolean;  // Upload status
  error?: string;       // Upload error message
}

// Define option sets
const externalWallTypeOptions = [
  { value: "", label: "Select Wall Type" },
  { value: "SG", label: "Stone: granite or whinstone" },
  { value: "SS", label: "Stone: sandstone or limestone" },
  { value: "SO", label: "Solid Brick" },
  { value: "CO", label: "Cob" },
  { value: "CA", label: "Cavity" },
  { value: "TI", label: "Timber Frame" },
  { value: "SY", label: "System Build" },
  { value: "CW", label: "Curtain Wall" },
];

const externalWallConditionOptions = [
  { value: "", label: "Select List Value(s)" },
  { value: "good", label: "Good" },
  { value: "fair", label: "Fair (Minor defects)" },
  { value: "poor", label: "Poor (Significant defects)" },
];

const roofTypeOptions = [
  { value: "pitched", label: "Pitched" },
  { value: "flat", label: "Flat" },
  { value: "thatched", label: "Thatched" },
  { value: "sloping", label: "Sloping" },
  { value: "dwelling_above", label: "Dwelling Above" },
  { value: "another_dwelling_above", label: "Another Dwelling Above" },
];

const wallConstructionTypeOptions = [
  { value: "", label: "Select Wall Type" },
  { value: "SG", label: "Stone: granite or whinstone" },
  { value: "SS", label: "Stone: sandstone or limestone" },
  { value: "SO", label: "Solid Brick" },
  { value: "CO", label: "Cob" },
  { value: "CA", label: "Cavity" },
  { value: "TI", label: "Timber Frame" },
  { value: "SY", label: "System Build" },
  { value: "CW", label: "Curtain Wall" },
];

const mainConstructionWallTypeOptions = [
  { value: "", label: "Select List Value(s)" },
  { value: "cavity_gt_50", label: ">50% Cavity" },
  { value: "solid_gt_50", label: ">50% Solid" },
  { value: "mixed", label: "Mixed/Uncertain" },
];

const floorConstructionOptions = [
  { value: "", label: "Select Type" },
  { value: "suspended-timber", label: "Suspended Timber" },
  { value: "solid-concrete", label: "Solid Concrete" },
  { value: "another_dwelling_below", label: "Another Dwelling Below" },
];

// Updated FormData state to include all new fields
interface PropertyFrontFormData {
  buildForm: string; // Existing
  buildingOrientation: string; // Building orientation in degrees
  externalWallType: string;
  externalWallCondition: string;
  hasWallDefects: boolean | null;
  roofType: string;
  roofCondition: string; // good, average, or bad
  gutteringCondition: string; // good, average, or poor
  propertyWallConstructionType: string;
  propertyMainConstructionWallType: string;
  isWallInsulated: boolean | null;
  wallThickness: string; // Wall thickness in mm
  // Floor construction fields
  floorConstructionType: string;
  adequateSubFloorVentilation: boolean | null;
  evidenceOfFloorDampness: boolean | null;
  dpcPresent: boolean | null;
  dpcVisible: boolean | null;
  // Add notes fields if necessary for "Other" options or general comments
  additionalNotes: string; 
}

// Updated Photos state to use DisplayablePhotoData for arrays
interface PropertyFrontPhotos {
  detachmentType: PhotoField | null; // Existing (single image)
  orientationImage: DisplayablePhotoData[]; // Building orientation (multiple images)
  frontElevationImage: PhotoField | null; // New (single image)
  roofTypeEvidenceImages: DisplayablePhotoData[]; // New (multiple images)
  gutteringDrainageImages: DisplayablePhotoData[]; // New (multiple images)
  wallTypeEvidenceImage: PhotoField | null; // New (single image)
  wallThicknessFromDoor: DisplayablePhotoData[]; // Wall thickness from door (multiple images, min 2)
  wallThicknessFromWindow: DisplayablePhotoData[]; // Wall thickness from window (multiple images, min 2)
  wallInsulationImages: DisplayablePhotoData[]; // Wall insulation evidence (multiple images, min 3)
  dpcEvidenceImages: DisplayablePhotoData[]; // DPC Evidence (multiple images)
  wallDefectImages: DisplayablePhotoData[]; // Wall defect images (multiple images)
  floorConstructionEvidenceImages: DisplayablePhotoData[]; // Floor construction evidence (multiple images)
}

const CustomPropertyFrontIcon: React.FC<{ size?: number, className?: string }> = ({ size = 24, className = '' }) => (
  <img src={PropertyFrontIconSrc} alt="Property Front" style={{ width: size, height: size }} className={className} />
);

const PropertyFrontForm: React.FC<PropertyFrontFormProps> = ({
  onNavigateNext,
  onNavigateBack,
  onBuildFormChange,
  floorPlanId,
  surveyData,
  surveyId,
  onDataUpdate,
  initialBuildForm,
  initialExposedSide,
  autosave,
  onSaveImmediately,
  onGalleryStateChange,
  customFields = [],
  hiddenFields = [],
  hiddenSections = [],
  sectionOrder = [],
  builtInFieldOrder = []
}) => {
  const [buildForm, setBuildForm] = useState<string>(initialBuildForm || surveyData?.property_front?.buildForm || '');
  const [exposedSide, setExposedSide] = useState<'left' | 'right' | ''>(initialExposedSide || '');
  const [selectedGallery, setSelectedGallery] = useState<string | null>(null);
  const [showCompass, setShowCompass] = useState(false);
  const [orientationMode, setOrientationMode] = useState<'tool' | 'manual'>('manual');

  // Initialize photo upload queue with explicit surveyId and property name for better file organization
  const propertyName = surveyData ? getRetrofitSurveyAddress(surveyData) : undefined;
  const { queuePhotoUpload } = useRetrofitPhotoUpload(
    surveyId || surveyData?.id || floorPlanId,
    propertyName
  );

  const [formData, setFormData] = useState({
    externalWallType: surveyData?.property_front?.externalWallType || '',
    buildForm: surveyData?.property_front?.buildForm || '',
    buildingOrientation: surveyData?.property_front?.buildingOrientation || surveyData?.initial_questionnaire?.buildingOrientation || '',
    externalWallCondition: surveyData?.property_front?.externalWallCondition || '',
    hasWallDefects: surveyData?.property_front?.hasWallDefects ?? null,
    roofType: surveyData?.property_front?.roofType || '',
    roofCondition: surveyData?.property_front?.roofCondition || '',
    hasExternalRoofDefects: surveyData?.property_front?.hasExternalRoofDefects ?? null,
    gutteringCondition: surveyData?.property_front?.gutteringCondition || '',
    propertyWallConstructionType: surveyData?.property_front?.propertyWallConstructionType || '',
    propertyMainConstructionWallType: surveyData?.property_front?.propertyMainConstructionWallType || '',
    isWallInsulated: surveyData?.property_front?.isWallInsulated ?? null,
    wallThickness: surveyData?.property_front?.wallThickness || '',
    floorConstructionType: surveyData?.property_front?.floorConstructionType || '',
    adequateSubFloorVentilation: surveyData?.property_front?.adequateSubFloorVentilation ?? null,
    evidenceOfFloorDampness: surveyData?.property_front?.evidenceOfFloorDampness ?? null,
    dpcPresent: surveyData?.property_front?.dpcPresent ?? null,
    dpcVisible: surveyData?.property_front?.dpcVisible ?? null,
    additionalNotes: surveyData?.property_front?.additionalNotes || '',
  });

  const [photos, setPhotos] = useState({
    detachmentType: [] as DisplayablePhotoData[],
    orientationImage: [] as DisplayablePhotoData[],
    frontElevationImage: [] as DisplayablePhotoData[],
    roofTypeEvidenceImages: [] as DisplayablePhotoData[],
    externalRoofDefectsImages: [] as DisplayablePhotoData[],
    gutteringDrainageImages: [] as DisplayablePhotoData[],
    wallTypeEvidenceImage: [] as DisplayablePhotoData[],
    wallThicknessFromDoor: [] as DisplayablePhotoData[],
    wallThicknessFromWindow: [] as DisplayablePhotoData[],
    wallInsulationImages: [] as DisplayablePhotoData[],
    dpcEvidenceImages: [] as DisplayablePhotoData[],
    wallDefectImages: [] as DisplayablePhotoData[],
    floorConstructionEvidenceImages: [] as DisplayablePhotoData[],
  });

  // State for custom field values
  const [customFieldData, setCustomFieldData] = useState<Record<string, any>>({});

  // Debounce timer for dropdown autosave (defined early for use in useEffect)
  const autosaveTimerRef = useRef<NodeJS.Timeout | null>(null);
  // Track recent changes to prevent sync during immediate autosaves
  const recentChangeRef = useRef(false);
  // Track last synced data to prevent loops
  const lastSyncedDataRef = useRef<string>('');
  // Track photos that have been processed for migration to prevent re-processing
  const migratedPhotoIdsRef = useRef<Set<string>>(new Set());
  // Track if we've run migration to prevent React Strict Mode double-execution
  const migrationRunOnceRef = useRef(false);
  // Track initial mount to prevent setState during render
  const isInitialMountRef = useRef(true);

  // Restore buildForm and exposedSide from surveyData
  useEffect(() => {
    const restoredBuildForm = surveyData?.property_front?.buildForm;
    const restoredExposedSide = surveyData?.property_front?.exposedSide;
    
    if (restoredBuildForm) {
      console.log('[PropertyFrontForm] Restoring buildForm from surveyData:', restoredBuildForm);
      setBuildForm(restoredBuildForm);
    }
    if (restoredExposedSide === 'left' || restoredExposedSide === 'right') {
      console.log('[PropertyFrontForm] Restoring exposedSide from surveyData:', restoredExposedSide);
      setExposedSide(restoredExposedSide);
    }
    
    // Notify parent of restored values so tabs show/hide correctly
    if (restoredBuildForm) {
      let side: 'left' | 'right' | 'both' | 'none' | '' = '';
      const needsSideSelection = restoredBuildForm === 'semi-detached' || restoredBuildForm === 'end-terrace' || restoredBuildForm === 'enclosed-end-terrace';

      if (restoredBuildForm === 'detached') {
        side = 'both';
      } else if (restoredBuildForm === 'mid-terrace' || restoredBuildForm === 'enclosed-mid-terrace') {
        side = 'none';
      } else if (needsSideSelection) {
        side = restoredExposedSide || '';
      }
      
      console.log('[PropertyFrontForm] Calling onBuildFormChange with restored values:', { buildForm: restoredBuildForm, side });
      onBuildFormChange(restoredBuildForm, side);
    }
  }, [surveyData?.property_front?.buildForm, surveyData?.property_front?.exposedSide, onBuildFormChange]);

  // Restore photos from surveyData on mount
  useEffect(() => {
    if (surveyData?.property_front) {
      // Check if any photos are currently uploading - don't update state during upload to prevent flash
      const hasUploadingPhotos = Object.values(photos).some(photoField => {
        if (Array.isArray(photoField)) {
          return photoField.some((photo: any) => photo.uploading === true);
        }
        return false;
      });
      
      if (hasUploadingPhotos) {
        console.log('[PropertyFrontForm] Skipping photo restore - uploads in progress (prevents flash)');
        return;
      }
      
      // Helper function to ensure data is always an array and clear stale uploading flags
      const ensureArray = (data: any): DisplayablePhotoData[] => {
        if (!data) return [];
        if (Array.isArray(data)) {
          // Clear stale "uploading" flags from previous sessions
          return data.map(photo => ({
            ...photo,
            uploading: false,  // Reset uploading flag
            error: undefined    // Clear any errors
          }));
        }
        // Convert old single-photo format to array
        if (data.id && data.dataUrl) return [{ ...data, displayId: data.id, uploading: false }];
        return [];
      };

      const restored: any = {
        detachmentType: ensureArray(surveyData.property_front.detachmentType),
        orientationImage: ensureArray(surveyData.property_front.orientationImage || surveyData.initial_questionnaire?.photos?.orientationImage),
        frontElevationImage: ensureArray(surveyData.property_front.frontElevationImage),
        roofTypeEvidenceImages: ensureArray(surveyData.property_front.roofTypeEvidenceImages),
        externalRoofDefectsImages: ensureArray(surveyData.property_front.externalRoofDefectsImages),
        gutteringDrainageImages: ensureArray(surveyData.property_front.gutteringDrainageImages),
        wallTypeEvidenceImage: ensureArray(surveyData.property_front.wallTypeEvidenceImage),
        wallThicknessFromDoor: ensureArray(surveyData.property_front.wallThicknessFromDoor || surveyData.property_front.wallThicknessImages),
        wallThicknessFromWindow: ensureArray(surveyData.property_front.wallThicknessFromWindow),
        wallInsulationImages: ensureArray((surveyData.property_front as any).wallInsulationImages),
        dpcEvidenceImages: ensureArray(surveyData.property_front.dpcEvidenceImages),
        wallDefectImages: ensureArray(surveyData.property_front.wallDefectImages),
        floorConstructionEvidenceImages: ensureArray(surveyData.property_front.floorConstructionEvidenceImages),
      };
      console.log('[PropertyFrontForm] Restoring photos from surveyData (uploading flags cleared):', restored);
      setPhotos(restored);
    }
  }, [surveyData?.property_front]); // Removed photos from deps to prevent infinite loop

  // Load custom field values from surveyData
  useEffect(() => {
    if (surveyData?.property_front && customFields && customFields.length > 0) {
      const existingData: Record<string, any> = {};
      customFields.forEach(field => {
        const value = (surveyData.property_front as any)?.[field.id];
        if (value !== undefined) {
          existingData[field.id] = value;
        }
      });
      if (Object.keys(existingData).length > 0) {
        setCustomFieldData(existingData);
      }
    }
  }, [surveyData?.property_front, customFields]);

  // Auto-convert base64 photos to CDN URLs on load (backward compatibility)
  useEffect(() => {
    if (!photos || !onDataUpdate || !queuePhotoUpload) {
      return;
    }
    
    // Quick pre-check: Do we have ANY photos that need migration?
    let needsMigration = false;
    (Object.keys(photos) as Array<keyof typeof photos>).forEach(photoType => {
      const photoArray = photos[photoType] as DisplayablePhotoData[];
      if (!Array.isArray(photoArray)) return;
      
      photoArray.forEach(photo => {
        // Photo needs migration if it has base64 but no URL
        if ((photo.dataUrl || photo.base64String) && (!photo.url || photo.url === '')) {
          needsMigration = true;
        }
      });
    });
    
    // If no photos need migration, skip entirely
    if (!needsMigration) {
      console.log('[PropertyFrontForm] ✅ All photos already have CDN URLs, skipping migration');
      return;
    }
    
    // Prevent React Strict Mode double-execution
    if (migrationRunOnceRef.current) {
      console.log('[PropertyFrontForm] ⏭️ Migration already executed (Strict Mode protection)');
      return;
    }
    
    const currentSurveyId = surveyId || surveyData?.id;
    console.log('[PropertyFrontForm] 🚀 Starting migration for survey:', currentSurveyId, {
      photosInRef: migratedPhotoIdsRef.current.size
    });
    
    // Mark as run to prevent React Strict Mode double-execution
    migrationRunOnceRef.current = true;
    
    let hasBase64Photos = false;
    let totalPhotosChecked = 0;
    
    // Check all photo fields for base64 data
    (Object.keys(photos) as Array<keyof typeof photos>).forEach(photoType => {
      const photoArray = photos[photoType] as DisplayablePhotoData[];
      if (!Array.isArray(photoArray)) return;
      
      photoArray.forEach(photo => {
        totalPhotosChecked++;
        
        // Skip if already processed or being uploaded
        if (migratedPhotoIdsRef.current.has(photo.displayId) || photo.uploading) {
          console.log(`[PropertyFrontForm] ⏭️ Photo ${photo.displayId} already in ref or uploading, skipping`);
          return;
        }
        
        // If photo has base64 but no CDN URL, it needs migration
        // Check for empty string, null, or undefined URL
        const needsMigration = (photo.dataUrl || photo.base64String) && (!photo.url || photo.url === '');
        
        // Debug: log photo structure
        console.log(`[PropertyFrontForm] 🔍 Checking photo for migration:`, {
          displayId: photo.displayId,
          hasDataUrl: !!photo.dataUrl,
          hasBase64String: !!photo.base64String,
          hasUrl: !!photo.url,
          url: photo.url,
          needsMigration
        });
        
        if (needsMigration) {
          hasBase64Photos = true;
          
          // Mark as processed to prevent re-migration
          migratedPhotoIdsRef.current.add(photo.displayId);
          
          const dataUrl = photo.dataUrl || `data:image/${photo.format};base64,${photo.base64String}`;
          console.log(`[PropertyFrontForm] 🔄 Migrating base64 photo to CDN:`, photo.displayId);
          
          // Capture photoType with correct type for closure
          const capturedPhotoType = photoType;
          
          // Check if queuePhotoUpload exists
          if (!queuePhotoUpload) {
            console.error(`[PropertyFrontForm] ❌ queuePhotoUpload is not available!`);
            return;
          }
          
          console.log(`[PropertyFrontForm] 📤 Calling queuePhotoUpload for ${photo.displayId}`);
          
          // Queue for upload to get CDN URL
          queuePhotoUpload(
            photo.displayId,
            dataUrl,
            'property_front',
            capturedPhotoType,
            // On success: replace base64 with CDN URL
            async (url: string) => {
              setPhotos(current => {
                const updatedPhotoState = {
                  ...current,
                  [capturedPhotoType]: (current[capturedPhotoType] as DisplayablePhotoData[]).map(p => 
                    p.displayId === photo.displayId 
                      ? { 
                          displayId: p.displayId,
                          format: p.format,
                          url,
                          uploading: false,
                          error: undefined
                          // ✨ Base64 removed - now using CDN URL
                        }
                      : p
                  )
                };
                
                // Save migrated data to DB
                onDataUpdate(prevData => ({
                  ...prevData,
                  property_front: {
                    ...prevData.property_front,
                    ...formData,
                    ...updatedPhotoState
                  }
                }), 'property_front');
                
                return updatedPhotoState;
              });
              console.log(`[PropertyFrontForm] ✅ Migration complete: ${photo.displayId} → CDN URL`);
            },
            // On error: keep base64 as fallback
            (error: string) => {
              console.error(`[PropertyFrontForm] ⚠️ Migration failed for ${photo.displayId}, keeping base64:`, error);
            }
          );
        }
      });
    });
    
    if (hasBase64Photos) {
      console.log('[PropertyFrontForm] 🔄 Starting automatic base64 → CDN migration for existing photos');
    } else if (totalPhotosChecked > 0) {
      console.log(`[PropertyFrontForm] ✅ All ${totalPhotosChecked} photos already have CDN URLs, no migration needed`);
    }
    
    // Cleanup: reset flag when component unmounts or survey changes
    return () => {
      migrationRunOnceRef.current = false;
      migratedPhotoIdsRef.current.clear(); // Clear processed photos on cleanup
    };
  }, [photos, onDataUpdate, queuePhotoUpload, surveyId, surveyData?.id]); // Removed formData - not needed!

  // 📊 FILE SIZE ANALYSIS: Log detailed breakdown of Property Front data size
  const analyzePropertyFrontFileSize = useCallback(() => {
    if (!surveyData?.property_front) {
      console.log('📊 [PROPERTY FRONT SIZE] No data to analyze');
      return;
    }

    const data = surveyData.property_front;
    
    // Helper to calculate size in bytes
    const getSize = (obj: any): number => {
      return new Blob([JSON.stringify(obj)]).size;
    };

    // Helper to format bytes
    const formatBytes = (bytes: number): string => {
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
      return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
    };

    // Analyze each photo field
    const photoFields = [
      'detachmentType',
      'orientationImage',
      'frontElevationImage',
      'roofTypeEvidenceImages',
      'externalRoofDefectsImages',
      'gutteringDrainageImages',
      'wallTypeEvidenceImage',
      'wallThicknessFromDoor',
      'wallThicknessFromWindow',
      'dpcEvidenceImages',
      'wallDefectImages',
      'floorConstructionEvidenceImages'
    ];

    const photoAnalysis: Array<{
      field: string;
      count: number;
      totalSize: number;
      base64Count: number;
      base64Size: number;
      cdnCount: number;
      cdnSize: number;
      photos: Array<{
        displayId: string;
        hasBase64: boolean;
        hasCDN: boolean;
        size: number;
        uploading: boolean;
      }>;
    }> = [];

    let totalPhotos = 0;
    let totalSize = 0;
    let totalBase64Photos = 0;
    let totalBase64Size = 0;
    let totalCDNPhotos = 0;
    let totalCDNSize = 0;

    photoFields.forEach(fieldName => {
      const field = (data as any)[fieldName];
      if (!field) return;

      const photos = Array.isArray(field) ? field : [field];
      const fieldAnalysis = {
        field: fieldName,
        count: 0,
        totalSize: 0,
        base64Count: 0,
        base64Size: 0,
        cdnCount: 0,
        cdnSize: 0,
        photos: [] as Array<any>
      };

      photos.forEach((photo: any) => {
        if (!photo) return;

        const photoSize = getSize(photo);
        const hasBase64 = !!(photo.dataUrl || photo.base64String);
        const hasCDN = !!(photo.url && photo.url.startsWith('http'));

        fieldAnalysis.count++;
        fieldAnalysis.totalSize += photoSize;
        totalPhotos++;
        totalSize += photoSize;

        if (hasBase64) {
          fieldAnalysis.base64Count++;
          fieldAnalysis.base64Size += photoSize;
          totalBase64Photos++;
          totalBase64Size += photoSize;
        }

        if (hasCDN) {
          fieldAnalysis.cdnCount++;
          fieldAnalysis.cdnSize += photoSize;
          totalCDNPhotos++;
          totalCDNSize += photoSize;
        }

        fieldAnalysis.photos.push({
          displayId: photo.displayId || photo.id || 'unknown',
          hasBase64,
          hasCDN,
          size: photoSize,
          uploading: photo.uploading || false
        });
      });

      if (fieldAnalysis.count > 0) {
        photoAnalysis.push(fieldAnalysis);
      }
    });

    // Calculate non-photo data size with detailed breakdown
    const dataWithoutPhotos = { ...data };
    photoFields.forEach(field => {
      delete (dataWithoutPhotos as any)[field];
    });
    const nonPhotoSize = getSize(dataWithoutPhotos);
    
    // Analyze each non-photo field to find what's large
    const nonPhotoBreakdown: Array<{ field: string; size: number }> = [];
    Object.keys(dataWithoutPhotos).forEach(key => {
      const fieldValue = (dataWithoutPhotos as any)[key];
      const fieldSize = getSize(fieldValue);
      if (fieldSize > 1000) { // Only show fields > 1 KB
        nonPhotoBreakdown.push({ field: key, size: fieldSize });
      }
    });
    nonPhotoBreakdown.sort((a, b) => b.size - a.size);

    // Sort by total size descending
    photoAnalysis.sort((a, b) => b.totalSize - a.totalSize);

    console.log('');
    console.log('📊 ========================================');
    console.log('📊 [PROPERTY FRONT] FILE SIZE ANALYSIS');
    console.log('📊 ========================================');
    console.log(`📊 Total Size: ${formatBytes(totalSize + nonPhotoSize)}`);
    console.log(`📊 Non-Photo Data: ${formatBytes(nonPhotoSize)}`);
    
    // Show non-photo breakdown if unusually large
    if (nonPhotoSize > 10000 && nonPhotoBreakdown.length > 0) {
      console.warn(`   ⚠️ Non-photo data is unusually large! Breakdown:`);
      nonPhotoBreakdown.forEach(item => {
        console.warn(`   ├─ ${item.field}: ${formatBytes(item.size)}`);
      });
    }
    
    console.log(`📊 Photo Data: ${formatBytes(totalSize)}`);
    console.log('📊 ========================================');
    console.log(`📊 Total Photos: ${totalPhotos}`);
    console.log(`📊 Photos with Base64: ${totalBase64Photos} (${formatBytes(totalBase64Size)})`);
    console.log(`📊 Photos with CDN: ${totalCDNPhotos} (${formatBytes(totalCDNSize)})`);
    console.log('📊 ========================================');

    // Log each photo field
    photoAnalysis.forEach(field => {
      const emoji = field.base64Size > 10000 ? '🔴' : field.base64Size > 0 ? '🟡' : '🟢';
      console.log(`${emoji} ${field.field}: ${formatBytes(field.totalSize)} (${field.count} photos)`);
      console.log(`   ├─ Base64: ${field.base64Count} photos (${formatBytes(field.base64Size)})`);
      console.log(`   ├─ CDN: ${field.cdnCount} photos (${formatBytes(field.cdnSize)})`);
      
      // Show individual photos if field has base64
      if (field.base64Size > 0) {
        field.photos.forEach(photo => {
          if (photo.hasBase64) {
            const status = photo.uploading ? '⏳ uploading' : photo.hasCDN ? '⚠️ has CDN but still has base64' : '❌ base64 only';
            console.log(`   │  └─ ${photo.displayId.substring(0, 30)}: ${formatBytes(photo.size)} (${status})`);
          }
        });
      }
    });

    console.log('📊 ========================================');

    // Show recommendations
    if (totalBase64Size > 100000) {
      console.warn('⚠️ [RECOMMENDATION] Large base64 data detected!');
      console.warn(`   ${formatBytes(totalBase64Size)} could be saved by migrating to CDN`);
      
      // Check if photos have CDN URLs but still have base64
      const photosWithBothCDNAndBase64 = photoAnalysis.filter(f => 
        f.base64Size > 0 && f.cdnCount > 0
      ).length;
      
      if (photosWithBothCDNAndBase64 > 0) {
        console.warn('   ⚠️ Photos have CDN URLs but still have base64 data!');
        console.warn('   💡 Run cleanup: window.cleanupPropertyFrontBase64()');
      } else {
        console.warn('   Run photo migration or wait for photos to finish uploading');
      }
    }

    if (totalBase64Photos > 0) {
      console.warn(`⚠️ [ACTION NEEDED] ${totalBase64Photos} photos still have base64 data`);
      console.warn('   These photos should be uploaded to CDN to reduce file size');
    }

    if (totalBase64Photos === 0 && totalCDNPhotos > 0) {
      console.log('✅ [EXCELLENT] All photos are on CDN, no base64 bloat!');
    }

    console.log('📊 ========================================');
    console.log('');
  }, [surveyData?.property_front]);

  // 🧹 CLEANUP: Strip base64 from photos that already have CDN URLs
  const cleanupBase64FromCDNPhotos = useCallback(() => {
    console.log('🧹 [BASE64 CLEANUP] Checking for photos with CDN URLs that still have base64...');
    
    let cleanupNeeded = false;
    const cleanedPhotos = { ...photos };
    
    Object.keys(cleanedPhotos).forEach((fieldName) => {
      const field = cleanedPhotos[fieldName as keyof typeof photos];
      if (!Array.isArray(field)) return;
      
      const cleaned = field.map(photo => {
        // If photo has CDN URL but still has base64 data, strip the base64
        if (photo.url && photo.url.startsWith('http') && (photo.dataUrl || photo.base64String)) {
          console.log(`🧹 [BASE64 CLEANUP] Stripping base64 from ${fieldName}/${photo.displayId}`);
          cleanupNeeded = true;
          return {
            ...photo,
            dataUrl: undefined,
            base64String: undefined
          };
        }
        return photo;
      });
      
      cleanedPhotos[fieldName as keyof typeof photos] = cleaned as any;
    });
    
    if (cleanupNeeded) {
      console.log('🧹 [BASE64 CLEANUP] ✅ Cleaned photos, updating state and saving...');
      setPhotos(cleanedPhotos);
      
      // Save cleaned photos to database
      if (onDataUpdate) {
        setTimeout(() => {
          console.log('🧹 [BASE64 CLEANUP] 💾 Saving cleaned photos to database');
          onDataUpdate(prevData => ({
            ...prevData,
            property_front: {
              ...prevData.property_front,
              ...formData,
              ...cleanedPhotos
            }
          }), 'property_front');
        }, 100);
      }
    } else {
      console.log('🧹 [BASE64 CLEANUP] No cleanup needed, all photos are clean');
    }
  }, [photos, formData, onDataUpdate]);

  // Expose cleanup function to window for manual testing
  useEffect(() => {
    (window as any).cleanupPropertyFrontBase64 = cleanupBase64FromCDNPhotos;
    return () => {
      delete (window as any).cleanupPropertyFrontBase64;
    };
  }, [cleanupBase64FromCDNPhotos]);

  // 🧹 AUTO-CLEANUP: Automatically clean base64 from photos with CDN URLs on page load
  useEffect(() => {
    if (!photos || !onDataUpdate) return;
    
    // Check if any photos have CDN URLs but still have base64
    const needsCleanup = Object.values(photos).some(arr => 
      arr.some(p => p.url && p.url.startsWith('http') && (p.dataUrl || p.base64String))
    );
    
    if (needsCleanup) {
      console.log('🧹 [AUTO-CLEANUP] Detected photos with CDN + base64, running automatic cleanup in 3 seconds...');
      const timer = setTimeout(() => {
        cleanupBase64FromCDNPhotos();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [photos, onDataUpdate, cleanupBase64FromCDNPhotos]);
  
  // 🗑️ LEGACY CLEANUP: Remove old wallThicknessImages field if it exists
  useEffect(() => {
    if (!surveyData?.property_front || !onDataUpdate) return;
    
    const data = surveyData.property_front as any;
    if (data.wallThicknessImages) {
      console.warn('🗑️ [LEGACY CLEANUP] Found old wallThicknessImages field, removing...');
      console.warn(`   Field size: ${(JSON.stringify(data.wallThicknessImages).length / 1024).toFixed(2)} KB`);
      
      // Remove the legacy field
      const cleanedData = { ...data };
      delete cleanedData.wallThicknessImages;
      
      // Save cleaned data
      onDataUpdate(prevData => ({
        ...prevData,
        property_front: cleanedData
      }), 'property_front');
      
      console.log('🗑️ [LEGACY CLEANUP] ✅ Removed wallThicknessImages field');
    }
  }, [surveyData?.property_front, onDataUpdate]);

  // Run file size analysis when photos change
  useEffect(() => {
    if (photos && Object.values(photos).some(arr => arr.length > 0)) {
      // Delay to allow state to settle
      const timer = setTimeout(() => {
        analyzePropertyFrontFileSize();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [photos, analyzePropertyFrontFileSize]);

  // Sync surveyData prop changes back to form state (but not while autosave is pending or recently changed)
  useEffect(() => {
    // Don't sync if we have a pending autosave or just made a change
    if (surveyData?.property_front && !autosaveTimerRef.current && !recentChangeRef.current) {
      const incoming = JSON.stringify(surveyData.property_front);

      if (incoming !== lastSyncedDataRef.current) {
        lastSyncedDataRef.current = incoming;
        setFormData({
          buildForm: surveyData.property_front.buildForm || '',
          buildingOrientation: surveyData.property_front.buildingOrientation || '',
          externalWallType: surveyData.property_front.externalWallType || '',
          externalWallCondition: surveyData.property_front.externalWallCondition || '',
          hasWallDefects: surveyData.property_front.hasWallDefects ?? null,
          roofType: surveyData.property_front.roofType || '',
          roofCondition: surveyData.property_front.roofCondition || '',
          hasExternalRoofDefects: surveyData.property_front.hasExternalRoofDefects ?? null,
          gutteringCondition: surveyData.property_front.gutteringCondition || '',
          propertyWallConstructionType: surveyData.property_front.propertyWallConstructionType || '',
          propertyMainConstructionWallType: surveyData.property_front.propertyMainConstructionWallType || '',
          isWallInsulated: surveyData.property_front.isWallInsulated ?? null,
          wallThickness: surveyData.property_front.wallThickness || '',
          floorConstructionType: surveyData.property_front.floorConstructionType || '',
          adequateSubFloorVentilation: surveyData.property_front.adequateSubFloorVentilation ?? null,
          evidenceOfFloorDampness: surveyData.property_front.evidenceOfFloorDampness ?? null,
          dpcPresent: surveyData.property_front.dpcPresent ?? null,
          dpcVisible: surveyData.property_front.dpcVisible ?? null,
          additionalNotes: surveyData.property_front.additionalNotes || '',
        });
      }
    }
  }, [surveyData]);

  // useEffect to inform parent of changes
  useEffect(() => {
    // Skip on initial mount to prevent setState during render
    if (isInitialMountRef.current) {
      console.log('[PropertyFrontForm] Skipping onBuildFormChange on initial mount');
      isInitialMountRef.current = false;
      return;
    }
    
    console.log('[PropertyFrontForm] Build form or exposed side changed:', { buildForm, exposedSide });
    
    let side: 'left' | 'right' | 'both' | 'none' | '' = '';
    const needsSideSelection = buildForm === 'semi-detached' || buildForm === 'end-terrace' || buildForm === 'enclosed-end-terrace';

    if (buildForm === 'detached') {
      side = 'both';
    } else if (buildForm === 'mid-terrace' || buildForm === 'enclosed-mid-terrace') {
      side = 'none';
    } else if (needsSideSelection) {
      side = exposedSide;
    }
    
    console.log('[PropertyFrontForm] Calling onBuildFormChange:', { buildForm, side });
    onBuildFormChange(buildForm, side);
  }, [buildForm, exposedSide, onBuildFormChange]);

  // Added handleInputChange
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    if (name === 'buildForm') {
      // when build form changes, reset the user's side selection
      setExposedSide('');
    }

    // Mark recent change to prevent sync
    recentChangeRef.current = true;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Trigger immediate autosave via parent callback (no debouncing)
    if (onDataUpdate) {
      onDataUpdate(prev => ({
        ...prev,
        property_front: {
          ...prev.property_front,
          [name]: value
        }
      }), 'property_front');
      // Allow sync after autosave completes
      setTimeout(() => {
        recentChangeRef.current = false;
      }, 100);
    }
  };

  // Handle blur event - save immediately when user leaves input
  const handleInputBlur = () => {
    if (onSaveImmediately) {
      onSaveImmediately().catch(err => 
        console.error('[PropertyFrontForm] Immediate save on blur failed:', err)
      );
    }
  };

  const handleBooleanChange = (name: keyof typeof formData, value: boolean) => {
    // Mark recent change to prevent sync
    recentChangeRef.current = true;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear photos when setting to false
    if (name === 'hasWallDefects' && value === false) {
      setPhotos(p => ({ ...p, wallDefectImages: [] }));
    }

    // Trigger autosave via parent callback
    if (onDataUpdate) {
      onDataUpdate(prev => ({
        ...prev,
        property_front: {
          ...prev.property_front,
          [name]: value
        }
      }), 'property_front');
      // Allow sync after autosave completes
      setTimeout(() => {
        recentChangeRef.current = false;
      }, 100);
    }
  };

  // Handle custom field changes
  const handleCustomFieldChange = (fieldId: string, value: any) => {
    setCustomFieldData(prev => ({ ...prev, [fieldId]: value }));
    
    // Trigger autosave via parent callback
    if (onDataUpdate) {
      onDataUpdate(prev => ({
        ...prev,
        property_front: {
          ...prev.property_front,
          [fieldId]: value
        }
      }), 'property_front');
    }
  };

  // Added handlePhotosCaptured - now supports multiple photos for all fields with background upload
  const handlePhotosCaptured = (photoType: keyof typeof photos, incomingPhotos: PhotoData[]) => {
    const newPhotoData = incomingPhotos.map((p, idx) => {
      const displayId = `${photoType}_${Date.now()}_${idx}`;
      const dataUrl = `data:image/${p.format};base64,${p.base64String}`;
      
      return {
        ...p,
        displayId,
        dataUrl,
        uploading: true, // Mark as uploading
        fileName: undefined,
        url: undefined,
        error: undefined
      };
    });

    setPhotos(prev => {
      const existingPhotos = Array.isArray(prev[photoType]) ? prev[photoType] : [];
      const updatedPhotos = {
        ...prev,
        [photoType]: [...existingPhotos, ...newPhotoData]
      };

      // Queue photos for background upload to Supabase
      newPhotoData.forEach(photo => {
        console.log(`[PropertyFrontForm] 📤 Queueing upload for ${photoType}:`, {
          displayId: photo.displayId,
          hasDataUrl: !!photo.dataUrl,
          dataUrlLength: photo.dataUrl?.length
        });
        
        queuePhotoUpload(
          photo.displayId,
          photo.dataUrl,
          'property_front',
          photoType,
          // On success: update photo with URL and trigger immediate save
          async (url: string) => {
            console.log(`[PropertyFrontForm] ✅ Upload SUCCESS for ${photoType}:`, {
              displayId: photo.displayId,
              cdnUrl: url,
              photoType
            });
            // Update photos state first
            setPhotos(current => {
              const currentPhotos = Array.isArray(current[photoType]) ? current[photoType] : [];
              const updatedPhotos = currentPhotos.map(p => 
                p.displayId === photo.displayId 
                  ? { 
                      displayId: p.displayId,
                      format: p.format,
                      fileName: p.fileName,
                      dataUrl: p.dataUrl, // Keep dataUrl for immediate display
                      url, // CDN URL
                      uploading: false,
                      error: undefined
                    }
                  : p
              );
              
              console.log(`[PropertyFrontForm] 🎯 Photo ${photo.displayId} updated with CDN URL:`, {
                photoType,
                url,
                updatedPhoto: updatedPhotos.find(p => p.displayId === photo.displayId)
              });
              
              return {
                ...current,
                [photoType]: updatedPhotos
              };
            });
            
            // Save to DB with CDN URL (outside of setState to avoid React warning)
            if (onDataUpdate) {
              setTimeout(() => {
                setPhotos(currentPhotos => {
                  const photoData = currentPhotos[photoType];
                  console.log(`[PropertyFrontForm] 💾 Saving ${photoType} to database:`, {
                    photoType,
                    count: photoData?.length,
                    photos: photoData?.map((p: any) => ({
                      displayId: p.displayId,
                      hasUrl: !!p.url,
                      hasDataUrl: !!p.dataUrl,
                      url: p.url?.substring(0, 50) + '...',
                      uploading: p.uploading
                    }))
                  });
                  onDataUpdate(prevData => ({
                    ...prevData,
                    property_front: {
                      ...prevData.property_front,
                      ...formData,
                      [photoType]: currentPhotos[photoType]
                    }
                  }), 'property_front');
                  return currentPhotos;
                });
              }, 0);
            }
            
            console.log(`[PropertyFrontForm] ✅ Photo ${photo.displayId} uploaded successfully, base64 data cleaned:`, url);
          },
          // On error: mark photo with error and save with base64 as fallback
          (error: string) => {
            // Update photos state with error
            setPhotos(current => {
              const currentPhotos = Array.isArray(current[photoType]) ? current[photoType] : [];
              return {
                ...current,
                [photoType]: currentPhotos.map(p => 
                  p.displayId === photo.displayId 
                    ? { ...p, uploading: false, error }
                    : p
                )
              };
            });
            
            // Save to DB with base64 as fallback (outside of setState to avoid React warning)
            if (onDataUpdate) {
              setTimeout(() => {
                setPhotos(currentPhotos => {
                  console.log(`[PropertyFrontForm] Photo upload failed, saving with base64 for offline use`);
                  onDataUpdate(prevData => ({
                    ...prevData,
                    property_front: {
                      ...prevData.property_front,
                      ...formData,
                      [photoType]: currentPhotos[photoType]
                    }
                  }), 'property_front');
                  return currentPhotos;
                });
              }, 0);
            }
            
            console.error(`[PropertyFrontForm] Photo ${photo.displayId} upload failed:`, error);
          }
        );
      });

      // Don't immediately save to DB - photos will be saved after successful upload with CDN URLs
      // This prevents storing large base64 data in the database
      console.log('[PropertyFrontForm] Photos captured, queued for upload. Will save after CDN upload completes.');

      return updatedPhotos;
    });
  };

  // Note: Removed immediate save on unmount. Partial saves on data changes are sufficient.
  // The immediate full save was causing errors due to base64 data in other survey sections.

  const removePhoto = async (photoType: keyof typeof photos, displayIdToRemove?: string) => {
    console.log(`[PropertyFrontForm] 🗑️ Removing photo from ${photoType}, displayId: ${displayIdToRemove}`);
    
    // Find the photo to delete from storage
    if (displayIdToRemove) {
      const currentPhotoState = Array.isArray(photos[photoType]) ? photos[photoType] : [];
      const photoToDelete = currentPhotoState.find(p => p.displayId === displayIdToRemove);
      
      if (photoToDelete) {
        console.log(`[PropertyFrontForm] 🗑️ Found photo to delete:`, photoToDelete);
        
        // Try to delete from storage
        let filePathToDelete = photoToDelete.fileName;
        
        // If no fileName but has URL, extract path from CDN URL
        if (!filePathToDelete && photoToDelete.url && photoToDelete.url.includes('/storage/v1/object/public/')) {
          const urlParts = photoToDelete.url.split('/storage/v1/object/public/')[1];
          if (urlParts) {
            const [bucket, ...pathParts] = urlParts.split('/');
            filePathToDelete = pathParts.join('/');
            console.log(`[PropertyFrontForm] 🗑️ Extracted path from URL: ${filePathToDelete}`);
          }
        }
        
        if (filePathToDelete) {
          try {
            console.log(`[PropertyFrontForm] 🗑️ Deleting from storage: ${filePathToDelete}`);
            await deletePhotoFromSupabase(filePathToDelete, 'retrofit-survey-images');
            console.log(`[PropertyFrontForm] ✅ Deleted from storage`);
          } catch (error) {
            console.warn(`[PropertyFrontForm] ⚠️ Failed to delete from storage:`, error);
            // Continue with state deletion even if storage deletion fails
          }
        } else {
          console.log(`[PropertyFrontForm] ℹ️ No fileName/URL found, skipping storage deletion (local photo only)`);
        }
      }
    }
    
    // Remove from state
    setPhotos(prev => {
      const currentPhotoState = Array.isArray(prev[photoType]) ? prev[photoType] : [];
      const filteredPhotos = displayIdToRemove
        ? currentPhotoState.filter(p => p.displayId !== displayIdToRemove)
        : [];
      const updatedPhotos = { ...prev, [photoType]: filteredPhotos };

      console.log(`[PropertyFrontForm] ✅ Removed photo from state, ${filteredPhotos.length} remaining in ${photoType}`);

      // Trigger immediate autosave with updated photos
      if (onDataUpdate) {
        setTimeout(() => {
          onDataUpdate(prevData => ({
            ...prevData,
            property_front: {
              ...prevData.property_front,
              ...formData,
              ...updatedPhotos
            }
          }), 'property_front');
        }, 50);
      }

      return updatedPhotos;
    });
  };

  const handleOpenGallery = (e: React.MouseEvent<HTMLButtonElement>, photoType: string) => {
    e.preventDefault();
    e.stopPropagation();

    // Blur any active input to dismiss keyboard
    if (document.activeElement && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    setSelectedGallery(photoType);
  };

  const handleCloseGallery = () => {
    setSelectedGallery(null);
  };

  const renderPhotoPreview = (photoDatas: DisplayablePhotoData[], photoType: keyof typeof photos, altTextPrefix: string) => {
    // Ensure photoDatas is an array
    const photoArray = Array.isArray(photoDatas) ? photoDatas : [];
    if (photoArray.length === 0) return null;

    // Use CDN URL if available, otherwise use dataUrl
    const latestPhoto = photoArray[photoArray.length - 1];
    const imageUrl = latestPhoto.url || latestPhoto.dataUrl;
    
    console.log(`[PropertyFrontForm] 🖼️ Rendering preview for ${photoType}:`, {
      photoCount: photoArray.length,
      latestPhoto: { displayId: latestPhoto.displayId, hasUrl: !!latestPhoto.url, hasDataUrl: !!latestPhoto.dataUrl },
      imageUrl: imageUrl?.substring(0, 100) + '...'
    });
    
    if (!imageUrl) return null;

    return (
      <button
        type="button"
        onClick={(e) => handleOpenGallery(e, photoType)}
        className="relative w-20 h-20 rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all hover:scale-105 ring-2 ring-slate-300 hover:ring-blue-400 flex-shrink-0 mt-2"
      >
        <img
          src={imageUrl}
          alt={altTextPrefix}
          className="w-full h-full object-cover"
        />
        <div className="absolute top-1 right-1 bg-gradient-to-r from-blue-500 to-indigo-600 text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg flex items-center gap-1">
          <Images size={12} />
          {photoArray.length}
        </div>
      </button>
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Basic validation for Wall Thickness Images (min 2 each)
    if (photos.wallThicknessFromDoor.length < 2) {
      alert("Please upload at least 2 images for Wall Thickness from Door.");
      return;
    }
    if (photos.wallThicknessFromWindow.length < 2) {
      alert("Please upload at least 2 images for Wall Thickness from Window.");
      return;
    }
    console.log('Property Front Data:', { floorPlanId, formData, photos });
    onNavigateNext();
  };

  const handleNavigateBack = () => {
    onNavigateBack();
  };

  const commonSelectClassName = "w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400";
  const photoButtonClass = "w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-semibold rounded-lg shadow-md hover:from-blue-600 hover:to-indigo-700 transition-all duration-200";
  const photoCaptureContainerClass = "p-4 border border-gray-200 rounded-lg bg-gray-50/30 space-y-2";
  const fieldWrapperClass = "space-y-2";
  const labelClass = "text-sm font-medium text-gray-700 flex items-center";
  const requiredMarker = <span className="text-red-500 mr-1">*</span>;
  const subLabelClass = "text-xs text-gray-500 ml-1";
  const booleanButtonClass = (isSelected: boolean | null | string, actualValue: boolean | string) =>
    `flex-1 py-3 rounded-lg border-2 ${isSelected === actualValue ? 'bg-yellow-100 border-yellow-400 text-yellow-700' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'} transition-colors font-medium`;

  const buildFormOptions = [
      { value: "", label: "Select List Value(s)"},
      { value: "detached", label: "Detached" },
      { value: "semi-detached", label: "Semi-detached" },
      { value: "mid-terrace", label: "Mid-terrace" },
      { value: "end-terrace", label: "End-terrace" },
      { value: "enclosed-mid-terrace", label: "Enclosed mid-terrace" },
      { value: "enclosed-end-terrace", label: "Enclosed end-terrace" },
  ];

  const conditionOptions = [
      { value: "", label: "Select Condition" },
      { value: "good", label: "Good" },
      { value: "average", label: "Average" },
      { value: "poor", label: "Poor" },
  ];

  const roofTypeOptions = [
      { value: "pitched", label: "Pitched" },
      { value: "flat", label: "Flat" },
      { value: "thatched", label: "Thatched" },
      { value: "sloping", label: "Sloping" },
      { value: "dwelling_above", label: "Dwelling Above" },
      { value: "another_dwelling_above", label: "Another Dwelling Above" },
  ];
  
  const propertyWallConstructionTypeOptions = [
      { value: "", label: "Select Construction Type" },
      { value: "brick", label: "Brick" },
      { value: "stone", label: "Stone" },
      { value: "render", label: "Render" },
  ];

  // ==============================================================================
  // DYNAMIC RENDERING SYSTEM (Like Initial Questionnaire)
  // ==============================================================================

  const isFieldHidden = (fieldId: string) => hiddenFields.includes(fieldId);

  const builtInSections = [
    {
      id: 'build_form_detachment',
      title: 'Build Form & Detachment',
      fields: ['buildForm', 'exposedSide', 'detachmentType', 'buildingOrientation', 'orientationImage']
    },
    {
      id: 'front_elevation',
      title: 'Front Elevation',
      fields: ['frontElevationImage', 'roofTypeEvidenceImages']
    },
    {
      id: 'wall_front',
      title: 'External Wall Details',
      fields: ['externalWallType', 'externalWallCondition', 'wallTypeEvidenceImage',
               'isWallInsulated', 'hasWallDefects', 'wallDefectImages']
    },
    {
      id: 'roof_front',
      title: 'Roof Details',
      fields: ['roofType', 'roofCondition']
    },
    {
      id: 'notes_front',
      title: 'Additional Notes',
      fields: ['additionalNotes']
    }
  ];

  const fieldSections = useMemo(() => {
    let orderedSections = [...builtInSections];
    
    if (sectionOrder && sectionOrder.length > 0) {
      orderedSections.sort((a, b) => {
        const aIndex = sectionOrder.indexOf(a.id);
        const bIndex = sectionOrder.indexOf(b.id);
        
        if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
        if (aIndex !== -1) return -1;
        if (bIndex !== -1) return 1;
        
        return 0;
      });
    }
    
    if (builtInFieldOrder && builtInFieldOrder.length > 0) {
      orderedSections.forEach(section => {
        section.fields.sort((a, b) => {
          const aIndex = builtInFieldOrder.indexOf(a);
          const bIndex = builtInFieldOrder.indexOf(b);
          
          if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
          if (aIndex !== -1) return -1;
          if (bIndex !== -1) return 1;
          
          return 0;
        });
      });
    }
    
    return orderedSections;
  }, [sectionOrder, builtInFieldOrder]);

  const renderField = (fieldId: string): JSX.Element | null => {
    if (isFieldHidden(fieldId)) return null;

    switch (fieldId) {
      case 'buildForm':
        return (
          <div key={fieldId} className={fieldWrapperClass}>
            <label className={labelClass}>{requiredMarker}Build Form</label>
            <div className="flex space-x-4 mt-2">
              {buildFormOptions.map(option => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => onBuildFormChange(option.value, exposedSide)}
                  className={booleanButtonClass(buildForm, option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        );

      case 'exposedSide':
        if (!['semi_detached', 'mid_terrace', 'end_terrace'].includes(buildForm)) return null;
        return (
          <div key={fieldId} className={fieldWrapperClass}>
            <label className={labelClass}>{requiredMarker}Which side is exposed?</label>
            <div className="flex space-x-4 mt-2">
              <button
                type="button"
                onClick={() => onBuildFormChange(buildForm, 'left')}
                className={booleanButtonClass(exposedSide, 'left')}
              >
                Left
              </button>
              <button
                type="button"
                onClick={() => onBuildFormChange(buildForm, 'right')}
                className={booleanButtonClass(exposedSide, 'right')}
              >
                Right
              </button>
            </div>
          </div>
        );

      case 'detachmentType':
        return (
          <div key={fieldId} className={photoCaptureContainerClass}>
            <label className={labelClass}>{requiredMarker}Detachment Type</label>
            <PhotoCapture
              onPhotosCaptured={(p) => handlePhotosCaptured('detachmentType', p)}
              maxPhotos={1}
              buttonLabel="Capture Detachment Type"
              buttonClass={photoButtonClass}
            />
            {renderPhotoPreview(photos.detachmentType, 'detachmentType', 'Detachment Type')}
          </div>
        );

      case 'buildingOrientation':
        return (
          <div key={fieldId} className={fieldWrapperClass}>
            <label className={labelClass}>
              {requiredMarker}Building Orientation
              <span className={subLabelClass}>(Front door direction in degrees)</span>
            </label>
            <div className="flex gap-2">
              <input
                type="number"
                name="buildingOrientation"
                value={formData.buildingOrientation}
                onChange={handleInputChange}
                min="0"
                max="360"
                placeholder="0-360"
                className={commonSelectClassName}
              />
              <button
                type="button"
                onClick={() => setShowCompass(true)}
                className="px-4 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center gap-2"
              >
                <Navigation size={18} />
                Compass
              </button>
            </div>
          </div>
        );

      case 'orientationImage':
        return (
          <div key={fieldId} className={photoCaptureContainerClass}>
            <label className={labelClass}>Orientation Image</label>
            <PhotoCapture
              onPhotosCaptured={(p) => handlePhotosCaptured('orientationImage', p)}
              maxPhotos={999}
              buttonLabel="Capture Orientation"
              buttonClass={photoButtonClass}
            />
            {renderPhotoPreview(photos.orientationImage, 'orientationImage', 'Orientation')}
          </div>
        );

      case 'frontElevationImage':
        return (
          <div key={fieldId} className={photoCaptureContainerClass}>
            <label className={labelClass}>{requiredMarker}Front Elevation</label>
            <PhotoCapture
              onPhotosCaptured={(p) => handlePhotosCaptured('frontElevationImage', p)}
              maxPhotos={1}
              buttonLabel="Capture Front Elevation"
              buttonClass={photoButtonClass}
            />
            {renderPhotoPreview(photos.frontElevationImage, 'frontElevationImage', 'Front Elevation')}
          </div>
        );

      case 'roofTypeEvidenceImages':
        return (
          <div key={fieldId} className={photoCaptureContainerClass}>
            <label className={labelClass}>
              {requiredMarker}Roof Type Evidence
              <span className={subLabelClass}>(Front elevation roof images)</span>
            </label>
            <PhotoCapture
              onPhotosCaptured={(p) => handlePhotosCaptured('roofTypeEvidenceImages', p)}
              maxPhotos={999}
              buttonLabel="Capture Roof Evidence"
              buttonClass={photoButtonClass}
            />
            {renderPhotoPreview(photos.roofTypeEvidenceImages, 'roofTypeEvidenceImages', 'Roof Evidence')}
          </div>
        );

      case 'externalWallType':
        return (
          <div key={fieldId} className={fieldWrapperClass}>
            <label htmlFor="externalWallType" className={labelClass}>
              {requiredMarker}External Wall Type
            </label>
            <select
              id="externalWallType"
              name="externalWallType"
              value={formData.externalWallType}
              onChange={handleInputChange}
              className={commonSelectClassName}
              required
            >
              {externalWallTypeOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        );

      case 'externalWallCondition':
        return (
          <div key={fieldId} className={fieldWrapperClass}>
            <label htmlFor="externalWallCondition" className={labelClass}>
              {requiredMarker}External Wall Condition
            </label>
            <select
              id="externalWallCondition"
              name="externalWallCondition"
              value={formData.externalWallCondition}
              onChange={handleInputChange}
              className={commonSelectClassName}
              required
            >
              {externalWallConditionOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        );

      case 'wallTypeEvidenceImage':
        return (
          <div key={fieldId} className={photoCaptureContainerClass}>
            <label className={labelClass}>{requiredMarker}Wall Type Evidence</label>
            <PhotoCapture
              onPhotosCaptured={(p) => handlePhotosCaptured('wallTypeEvidenceImage', p)}
              maxPhotos={999}
              buttonLabel="Capture Wall Type Evidence"
              buttonClass={photoButtonClass}
            />
            {renderPhotoPreview(photos.wallTypeEvidenceImage, 'wallTypeEvidenceImage', 'Wall Type Evidence')}
          </div>
        );

      case 'isWallInsulated':
        return (
          <div key={fieldId} className={fieldWrapperClass}>
            <label className={labelClass}>{requiredMarker}Is the Wall Insulated?</label>
            <div className="flex space-x-4 mt-2">
              <button
                type="button"
                onClick={() => handleBooleanChange('isWallInsulated', true)}
                className={booleanButtonClass(formData.isWallInsulated, true)}
              >
                Yes
              </button>
              <button
                type="button"
                onClick={() => handleBooleanChange('isWallInsulated', false)}
                className={booleanButtonClass(formData.isWallInsulated, false)}
              >
                No
              </button>
            </div>
          </div>
        );

      case 'hasWallDefects':
        return (
          <div key={fieldId} className={fieldWrapperClass}>
            <label className={labelClass}>Additional visible wall defects?</label>
            <div className="flex space-x-4 mt-2">
              <button
                type="button"
                onClick={() => handleBooleanChange('hasWallDefects', true)}
                className={booleanButtonClass(formData.hasWallDefects, true)}
              >
                Yes
              </button>
              <button
                type="button"
                onClick={() => handleBooleanChange('hasWallDefects', false)}
                className={booleanButtonClass(formData.hasWallDefects, false)}
              >
                No
              </button>
            </div>
          </div>
        );

      case 'wallDefectImages':
        if (!formData.hasWallDefects) return null;
        return (
          <div key={fieldId} className={photoCaptureContainerClass}>
            <label className={labelClass}>{requiredMarker}Wall Defect Images</label>
            <PhotoCapture
              onPhotosCaptured={(p) => handlePhotosCaptured('wallDefectImages', p)}
              maxPhotos={999}
              buttonLabel="Capture Wall Defects"
              buttonClass={photoButtonClass}
            />
            {renderPhotoPreview(photos.wallDefectImages, 'wallDefectImages', 'Wall Defect')}
          </div>
        );

      case 'roofType':
        return (
          <div key={fieldId} className={fieldWrapperClass}>
            <label htmlFor="roofType" className={labelClass}>
              {requiredMarker}Roof Type
            </label>
            <select
              id="roofType"
              name="roofType"
              value={formData.roofType}
              onChange={handleInputChange}
              className={commonSelectClassName}
              required
            >
              {roofTypeOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        );

      case 'roofCondition':
        return (
          <div key={fieldId} className={fieldWrapperClass}>
            <label htmlFor="roofCondition" className={labelClass}>
              {requiredMarker}Roof Condition
            </label>
            <select
              id="roofCondition"
              name="roofCondition"
              value={formData.roofCondition}
              onChange={handleInputChange}
              className={commonSelectClassName}
              required
            >
              {conditionOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        );

      case 'additionalNotes':
        return (
          <div key={fieldId} className={fieldWrapperClass}>
            <label htmlFor="additionalNotes" className={labelClass}>
              Additional Notes
            </label>
            <textarea
              id="additionalNotes"
              name="additionalNotes"
              value={formData.additionalNotes}
              onChange={handleInputChange}
              rows={3}
              placeholder="Enter any other relevant details..."
              className={commonSelectClassName}
            />
          </div>
        );

      default:
        return null;
    }
  };

  // ==============================================================================
  // END DYNAMIC RENDERING SYSTEM
  // ==============================================================================

  return (
    <div className="bg-white/80 backdrop-blur-sm p-6 sm:p-8 rounded-xl shadow-lg border border-yellow-200 min-h-[calc(100vh-12rem)] flex flex-col">
      <h2 className="text-xl sm:text-2xl font-bold text-gray-900 border-b-2 border-gray-200 pb-4 mb-6 flex items-center gap-3">
        <CustomPropertyFrontIcon />
        Property Front
      </h2>
      <form onSubmit={handleSubmit} className="space-y-6 flex-1 flex flex-col">
        <div className="flex-1 space-y-6">
          <fieldset className="border border-gray-300 p-4 rounded-lg">
            <legend className="text-md font-semibold text-gray-700 px-2">Build Form & Detachment</legend>
            <div className="space-y-4">
              {/* Build Form Selection - Button Based */}
              <div className={fieldWrapperClass}>
                <label className={labelClass}>
                  {requiredMarker}What is the build form of this property?
                </label>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  {buildFormOptions.filter(opt => opt.value !== '').map(option => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => {
                        console.log('[PropertyFrontForm] Button click - Setting buildForm:', option.value);
                        setBuildForm(option.value);
                        setExposedSide(''); // Reset exposed side
                        // Update survey data
                        if (onDataUpdate) {
                          onDataUpdate(prev => ({
                            ...prev,
                            property_front: {
                              ...prev.property_front,
                              buildForm: option.value,
                              exposedSide: '' // Reset
                            }
                          }), 'property_front');
                        }
                      }}
                      className={`py-3 px-4 rounded-lg border-2 transition-all font-medium ${
                        buildForm === option.value
                          ? 'bg-gradient-to-r from-orange-500 to-amber-500 border-orange-400 text-white shadow-lg transform scale-105'
                          : 'bg-white border-gray-300 text-gray-700 hover:border-orange-300 hover:bg-orange-50'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Exposed Side Selection - Button Based */}
              {(buildForm === 'semi-detached' || buildForm === 'end-terrace' || buildForm === 'enclosed-end-terrace') && (
                <div className={fieldWrapperClass}>
                  <label className={labelClass}>{requiredMarker}Which side is exposed?</label>
                  <div className="flex gap-3 mt-2">
                    <button
                      type="button"
                      onClick={() => {
                        console.log('[PropertyFrontForm] Setting exposedSide: left');
                        setExposedSide('left');
                        if (onDataUpdate) {
                          onDataUpdate(prev => ({
                            ...prev,
                            property_front: {
                              ...prev.property_front,
                              exposedSide: 'left'
                            }
                          }), 'property_front');
                        }
                        // Immediately notify parent to show Property Left tab
                        console.log('[PropertyFrontForm] Directly calling onBuildFormChange with:', buildForm, 'left');
                        onBuildFormChange(buildForm, 'left');
                      }}
                      className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all font-medium ${
                        exposedSide === 'left'
                          ? 'bg-gradient-to-r from-orange-500 to-amber-500 border-orange-400 text-white shadow-lg transform scale-105'
                          : 'bg-white border-gray-300 text-gray-700 hover:border-orange-300 hover:bg-orange-50'
                      }`}
                    >
                      Left
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        console.log('[PropertyFrontForm] Setting exposedSide: right');
                        setExposedSide('right');
                        if (onDataUpdate) {
                          onDataUpdate(prev => ({
                            ...prev,
                            property_front: {
                              ...prev.property_front,
                              exposedSide: 'right'
                            }
                          }), 'property_front');
                        }
                        // Immediately notify parent to show Property Right tab
                        console.log('[PropertyFrontForm] Directly calling onBuildFormChange with:', buildForm, 'right');
                        onBuildFormChange(buildForm, 'right');
                      }}
                      className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all font-medium ${
                        exposedSide === 'right'
                          ? 'bg-gradient-to-r from-orange-500 to-amber-500 border-orange-400 text-white shadow-lg transform scale-105'
                          : 'bg-white border-gray-300 text-gray-700 hover:border-orange-300 hover:bg-orange-50'
                      }`}
                    >
                      Right
                    </button>
                  </div>
                </div>
              )}

              <div className={photoCaptureContainerClass}>
                <label className={labelClass}>{requiredMarker}Detachment type Photo <span className="text-xs text-gray-500">(if semi-detached or end-terrace)</span></label>
                <PhotoCapture onPhotosCaptured={(p) => handlePhotosCaptured('detachmentType', p)} maxPhotos={999} buttonLabel="Capture Detachment Photo" buttonClass={photoButtonClass} />
                {renderPhotoPreview(photos.detachmentType, 'detachmentType', 'Detachment Type')}
              </div>

              {/* Building Orientation with Compass Tool */}
              <div className={fieldWrapperClass}>
                <label htmlFor="buildingOrientation" className={labelClass}>
                  {requiredMarker}Building Orientation (Main door to External Road)
                </label>
                
                {/* Show captured value prominently if exists */}
                {formData.buildingOrientation && (
                  <div className="mb-3 p-3 bg-gradient-to-r from-indigo-50 to-purple-50 border-2 border-indigo-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <p className="text-xs text-gray-600 font-medium mb-1">Captured Direction:</p>
                        <p className="text-lg font-bold text-indigo-700">{formData.buildingOrientation}</p>
                      </div>
                      {photos.orientationImage.length > 0 && (() => {
                        const latestPhoto = photos.orientationImage[photos.orientationImage.length - 1];
                        const imageUrl = latestPhoto?.url || latestPhoto?.dataUrl;
                        console.log('[PropertyFrontForm] 🧭 Orientation photo display:', {
                          count: photos.orientationImage.length,
                          hasLatestPhoto: !!latestPhoto,
                          hasUrl: !!latestPhoto?.url,
                          hasDataUrl: !!latestPhoto?.dataUrl,
                          imageUrl: imageUrl?.substring(0, 100)
                        });
                        return (
                          <button
                            type="button"
                            onClick={(e) => handleOpenGallery(e, 'orientationImage')}
                            className="ml-3 relative w-16 h-16 rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-all hover:scale-105 ring-2 ring-indigo-200 hover:ring-indigo-400 flex-shrink-0"
                          >
                            <img
                              src={imageUrl}
                              alt="Compass"
                              className="w-full h-full object-cover"
                              onError={() => console.error('[PropertyFrontForm] ❌ Failed to load orientation image:', imageUrl?.substring(0, 100))}
                              onLoad={() => console.log('[PropertyFrontForm] ✅ Orientation image loaded successfully')}
                            />
                            {photos.orientationImage.length > 1 && (
                              <div className="absolute top-1 right-1 bg-indigo-600 text-white text-xs font-bold px-1.5 py-0.5 rounded-full shadow-lg flex items-center gap-0.5">
                                <Images size={10} />
                                {photos.orientationImage.length}
                              </div>
                            )}
                          </button>
                        );
                      })()}
                      <Navigation className="w-6 h-6 text-indigo-300 ml-2" />
                    </div>
                  </div>
                )}
                
                {/* Mode Toggle Buttons */}
                <div className="flex rounded-md shadow-sm mb-3">
                  <button
                    type="button"
                    onClick={() => setOrientationMode('manual')}
                    className={`flex-1 px-4 py-2 text-sm font-medium rounded-l-md border ${orientationMode === 'manual' ? 'bg-blue-600 text-white border-blue-700 z-10' : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300'}`}
                  >
                    Manual Entry
                  </button>
                  <button
                    type="button"
                    onClick={() => setOrientationMode('tool')}
                    className={`flex-1 px-4 py-2 text-sm font-medium rounded-r-md border -ml-px ${orientationMode === 'tool' ? 'bg-blue-600 text-white border-blue-700 z-10' : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300'}`}
                  >
                    <Navigation className="w-4 h-4 inline mr-1" />
                    Compass Tool
                  </button>
                </div>

                {/* Manual Mode: Input + Photo Capture */}
                {orientationMode === 'manual' && (
                  <>
                    <input
                      type="text"
                      id="buildingOrientation"
                      name="buildingOrientation"
                      value={formData.buildingOrientation}
                      onChange={handleInputChange}
                      className={commonSelectClassName}
                      placeholder="e.g., Northeast (45.3°) or 45"
                    />
                    <p className="text-xs text-gray-500 mt-1">Enter direction (e.g., "North", "Northeast") or degrees (0-360° where 0° = North, 90° = East, 180° = South, 270° = West)</p>
                    
                    <div className="mt-3">
                      <label className="text-sm font-medium text-gray-700 block mb-2">Orientation Photo (Optional)</label>
                      <PhotoCapture onPhotosCaptured={(p) => handlePhotosCaptured('orientationImage', p)} maxPhotos={999} buttonLabel="Capture Orientation Photo" buttonClass={photoButtonClass} />
                      {renderPhotoPreview(photos.orientationImage, 'orientationImage', 'Building Orientation')}
                    </div>
                  </>
                )}

                {/* Tool Mode: Compass Measurer Button */}
                {orientationMode === 'tool' && (
                  <button
                    type="button"
                    onClick={() => setShowCompass(true)}
                    className="w-full bg-gradient-to-r from-indigo-600 to-purple-700 text-white py-3 px-4 rounded-lg font-semibold shadow-md hover:from-indigo-700 hover:to-purple-800 transition-all flex items-center justify-center gap-2"
                  >
                    <Navigation className="w-5 h-5" />
                    {formData.buildingOrientation ? 'Re-measure Direction' : 'Use Compass to Measure Direction'}
                  </button>
                )}
              </div>
            </div>
          </fieldset>

          <fieldset className="border border-gray-300 p-4 rounded-lg mt-4">
            <legend className="text-md font-semibold text-gray-700 px-2">Front Elevation</legend>
              <div className={photoCaptureContainerClass}>
                <label className={labelClass}>{requiredMarker}Front Elevation</label>
                <PhotoCapture onPhotosCaptured={(p) => handlePhotosCaptured('frontElevationImage', p)} maxPhotos={999} buttonLabel="Capture Front Elevation" buttonClass={photoButtonClass} />
                {renderPhotoPreview(photos.frontElevationImage, 'frontElevationImage', 'Front Elevation')}
              </div>
          </fieldset>

          <fieldset className="border border-gray-300 p-4 rounded-lg mt-4">
            <legend className="text-md font-semibold text-gray-700 px-2">External Wall Details</legend>
            <div className="space-y-4">
               <div className={fieldWrapperClass}>
                 <label htmlFor="externalWallType" className={labelClass}>{requiredMarker}What is the external wall type of this building part?</label>
                 <select id="externalWallType" name="externalWallType" value={formData.externalWallType} onChange={handleInputChange} className={commonSelectClassName} required>
                     {externalWallTypeOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                 </select>
               </div>

               <div className={fieldWrapperClass}>
                 <label className={labelClass}>{requiredMarker}Condition of external wall</label>
                 <div className="grid grid-cols-3 gap-x-4 gap-y-3 mt-2">
                   <label className="flex items-center space-x-2 p-2 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer">
                     <input type="radio" name="externalWallCondition" value="good" checked={formData.externalWallCondition === 'good'} onChange={handleInputChange} className="form-radio text-green-500 focus:ring-green-400"/>
                     <span className="text-sm text-gray-700">Good</span>
                   </label>
                   <label className="flex items-center space-x-2 p-2 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer">
                     <input type="radio" name="externalWallCondition" value="average" checked={formData.externalWallCondition === 'average'} onChange={handleInputChange} className="form-radio text-yellow-500 focus:ring-yellow-400"/>
                     <span className="text-sm text-gray-700">Average</span>
                   </label>
                   <label className="flex items-center space-x-2 p-2 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer">
                     <input type="radio" name="externalWallCondition" value="poor" checked={formData.externalWallCondition === 'poor'} onChange={handleInputChange} className="form-radio text-red-500 focus:ring-red-400"/>
                     <span className="text-sm text-gray-700">Poor</span>
                   </label>
                 </div>
               </div>

               <div className={fieldWrapperClass}>
                 <label className={labelClass}>{requiredMarker}Additional visible wall defects?</label>
                 <div className="flex space-x-4 mt-2">
                     <button type="button" onClick={() => handleBooleanChange('hasWallDefects', true)} className={booleanButtonClass(formData.hasWallDefects, true)}>Yes</button>
                     <button type="button" onClick={() => handleBooleanChange('hasWallDefects', false)} className={booleanButtonClass(formData.hasWallDefects, false)}>No</button>
                 </div>
               </div>

               {formData.hasWallDefects === true && (
                 <div className={photoCaptureContainerClass}>
                   <label className={labelClass}>{requiredMarker}Images of Wall Defects (Front)</label>
                   <PhotoCapture
                     onPhotosCaptured={(p) => handlePhotosCaptured('wallDefectImages', p)}
                     maxPhotos={999}
                     buttonLabel="Capture Wall Defect Images"
                     buttonClass={photoButtonClass}
                   />
                   {renderPhotoPreview(photos.wallDefectImages, 'wallDefectImages', 'Wall Defect')}
                 </div>
               )}
            </div>
          </fieldset>

          <fieldset className="border border-gray-300 p-4 rounded-lg mt-4">
            <legend className="text-md font-semibold text-gray-700 px-2">Roof Details</legend>
            <div className="space-y-4">
              <div className={fieldWrapperClass}>
                <label className={labelClass}>{requiredMarker}Roof Type</label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-3 mt-2">
                  {roofTypeOptions.map(opt => (
                    <label key={opt.value} className="flex items-center space-x-2 p-2 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer">
                      <input type="radio" name="roofType" value={opt.value} checked={formData.roofType === opt.value} onChange={handleInputChange} className="form-radio text-yellow-500 focus:ring-yellow-400"/>
                      <span className="text-sm text-gray-700">{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className={fieldWrapperClass}>
                <label className={labelClass}>{requiredMarker}Roof Condition</label>
                <div className="grid grid-cols-3 gap-x-4 gap-y-3 mt-2">
                  <label className="flex items-center space-x-2 p-2 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer">
                    <input type="radio" name="roofCondition" value="good" checked={formData.roofCondition === 'good'} onChange={handleInputChange} className="form-radio text-green-500 focus:ring-green-400"/>
                    <span className="text-sm text-gray-700">Good</span>
                  </label>
                  <label className="flex items-center space-x-2 p-2 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer">
                    <input type="radio" name="roofCondition" value="average" checked={formData.roofCondition === 'average'} onChange={handleInputChange} className="form-radio text-yellow-500 focus:ring-yellow-400"/>
                    <span className="text-sm text-gray-700">Average</span>
                  </label>
                  <label className="flex items-center space-x-2 p-2 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer">
                    <input type="radio" name="roofCondition" value="bad" checked={formData.roofCondition === 'bad'} onChange={handleInputChange} className="form-radio text-red-500 focus:ring-red-400"/>
                    <span className="text-sm text-gray-700">Bad</span>
                  </label>
                </div>
              </div>

              <div className={photoCaptureContainerClass}>
                <label className={labelClass}>{requiredMarker}Roof Type Evidence <span className={subLabelClass}>(All elevation roof images)</span></label>
                <PhotoCapture
                  onPhotosCaptured={(p) => handlePhotosCaptured('roofTypeEvidenceImages', p)}
                  maxPhotos={999}
                  buttonLabel="Capture Roof Evidence"
                  buttonClass={photoButtonClass}
                />
                {renderPhotoPreview(photos.roofTypeEvidenceImages, 'roofTypeEvidenceImages', 'Roof Evidence')}
              </div>

              <div className={fieldWrapperClass}>
                <label className={labelClass}>{requiredMarker}External Roof Defects</label>
                <div className="flex items-center gap-4 mt-2">
                  <button
                    type="button"
                    onClick={() => handleBooleanChange('hasExternalRoofDefects', true)}
                    className={`flex-1 px-4 py-2 rounded-md font-medium transition-colors ${
                      formData.hasExternalRoofDefects === true
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    onClick={() => handleBooleanChange('hasExternalRoofDefects', false)}
                    className={`flex-1 px-4 py-2 rounded-md font-medium transition-colors ${
                      formData.hasExternalRoofDefects === false
                        ? 'bg-gray-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    No
                  </button>
                </div>
              </div>

              {formData.hasExternalRoofDefects === true && (
                <div className={photoCaptureContainerClass}>
                  <label className={labelClass}>External Roof Defects Images</label>
                  <PhotoCapture
                    onPhotosCaptured={(p) => handlePhotosCaptured('externalRoofDefectsImages', p)}
                    maxPhotos={999}
                    buttonLabel="Capture Defect Images"
                    buttonClass={photoButtonClass}
                  />
                  {renderPhotoPreview(photos.externalRoofDefectsImages, 'externalRoofDefectsImages', 'Roof Defects')}
                </div>
              )}
            </div>
          </fieldset>

          <fieldset className="border border-gray-300 p-4 rounded-lg mt-4">
            <legend className="text-md font-semibold text-gray-700 px-2">Guttering and Drainage</legend>
            <div className="space-y-4">
              <div className={fieldWrapperClass}>
                <label className={labelClass}>{requiredMarker}Guttering and Drainage Condition</label>
                <div className="grid grid-cols-3 gap-x-4 gap-y-3 mt-2">
                  <label className="flex items-center space-x-2 p-2 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer">
                    <input type="radio" name="gutteringCondition" value="good" checked={formData.gutteringCondition === 'good'} onChange={handleInputChange} className="form-radio text-green-500 focus:ring-green-400"/>
                    <span className="text-sm text-gray-700">Good</span>
                  </label>
                  <label className="flex items-center space-x-2 p-2 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer">
                    <input type="radio" name="gutteringCondition" value="average" checked={formData.gutteringCondition === 'average'} onChange={handleInputChange} className="form-radio text-yellow-500 focus:ring-yellow-400"/>
                    <span className="text-sm text-gray-700">Average</span>
                  </label>
                  <label className="flex items-center space-x-2 p-2 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer">
                    <input type="radio" name="gutteringCondition" value="poor" checked={formData.gutteringCondition === 'poor'} onChange={handleInputChange} className="form-radio text-red-500 focus:ring-red-400"/>
                    <span className="text-sm text-gray-700">Poor</span>
                  </label>
                </div>
              </div>

              <div className={photoCaptureContainerClass}>
                <label className={labelClass}>{requiredMarker}Guttering and Drainage <span className={subLabelClass}>(Any foul drainage points)</span></label>
                <PhotoCapture
                  onPhotosCaptured={(p) => handlePhotosCaptured('gutteringDrainageImages', p)}
                  maxPhotos={999}
                  buttonLabel="Capture Drainage Images"
                  buttonClass={photoButtonClass}
                />
                {renderPhotoPreview(photos.gutteringDrainageImages, 'gutteringDrainageImages', 'Drainage Image')}
              </div>
            </div>
          </fieldset>

          <fieldset className="border border-gray-300 p-4 rounded-lg mt-4">
            <legend className="text-md font-semibold text-gray-700 px-2">Wall Type Evidence</legend>
            <div className="space-y-4">
              <div className={photoCaptureContainerClass}>
                <label className={labelClass}>{requiredMarker}Wall Type Evidence</label>
                <PhotoCapture
                  onPhotosCaptured={(p) => handlePhotosCaptured('wallTypeEvidenceImage', p)}
                  maxPhotos={999}
                  buttonLabel="Capture Wall Type Evidence"
                  buttonClass={photoButtonClass}
                />
                {renderPhotoPreview(photos.wallTypeEvidenceImage, 'wallTypeEvidenceImage', 'Wall Type Evidence')}
              </div>
            </div>
          </fieldset>

          <fieldset className="border border-gray-300 p-4 rounded-lg mt-4">
            <legend className="text-md font-semibold text-gray-700 px-2">Wall Insulation</legend>
            <div className="space-y-4">
              <div className={fieldWrapperClass}>
                <label className={labelClass}>{requiredMarker}Is the Wall Insulated?</label>
                <div className="flex space-x-4 mt-2">
                  <button type="button" onClick={() => handleBooleanChange('isWallInsulated', true)} className={`flex-1 py-3 rounded-lg border-2 ${formData.isWallInsulated === true ? 'bg-yellow-100 border-yellow-400 text-yellow-700' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'} transition-colors font-medium`}>Yes</button>
                  <button type="button" onClick={() => handleBooleanChange('isWallInsulated', false)} className={`flex-1 py-3 rounded-lg border-2 ${formData.isWallInsulated === false ? 'bg-yellow-100 border-yellow-400 text-yellow-700' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'} transition-colors font-medium`}>No</button>
                </div>
              </div>
              
              {formData.isWallInsulated === true && (
                <div className={photoCaptureContainerClass}>
                  <label className={labelClass}>{requiredMarker}Wall Insulation Evidence <span className={subLabelClass}>(Min. 3 Images)</span></label>
                  <PhotoCapture
                    onPhotosCaptured={(p) => handlePhotosCaptured('wallInsulationImages', p)}
                    maxPhotos={999}
                    buttonLabel="Capture Wall Insulation Evidence"
                    buttonClass={photoButtonClass}
                  />
                  {renderPhotoPreview(photos.wallInsulationImages, 'wallInsulationImages', 'Wall Insulation Evidence')}
                  {(!photos.wallInsulationImages || photos.wallInsulationImages.length < 3) && (
                    <p className="text-xs text-red-500 mt-1">Minimum 3 images required.</p>
                  )}
                </div>
              )}
            </div>
          </fieldset>

          <fieldset className="border border-gray-300 p-4 rounded-lg mt-4">
            <legend className="text-md font-semibold text-gray-700 px-2">Wall Thickness</legend>
            <div className="space-y-4">
              <div className={fieldWrapperClass}>
                <label htmlFor="wallThickness" className={labelClass}>{requiredMarker}Wall Thickness (mm)</label>
                <input
                  type="number"
                  id="wallThickness"
                  name="wallThickness"
                  value={formData.wallThickness}
                  onChange={handleInputChange}
                  placeholder="Enter wall thickness in mm"
                  className={commonSelectClassName}
                  min="0"
                  step="1"
                  required
                />
              </div>
              
              <div className={photoCaptureContainerClass}>
                <label className={labelClass}>{requiredMarker}Wall Thickness from Door <span className={subLabelClass}>(Min. 2 Images)</span></label>
                <PhotoCapture
                  onPhotosCaptured={(p) => handlePhotosCaptured('wallThicknessFromDoor', p)}
                  maxPhotos={999}
                  buttonLabel="Capture Wall Thickness from Door"
                  buttonClass={photoButtonClass}
                />
                {renderPhotoPreview(photos.wallThicknessFromDoor, 'wallThicknessFromDoor', 'Wall Thickness from Door')}
                {photos.wallThicknessFromDoor.length < 2 && (
                   <p className="text-xs text-red-500 mt-1">Minimum 2 images required.</p>
                )}
              </div>
              
              <div className={photoCaptureContainerClass}>
                <label className={labelClass}>{requiredMarker}Wall Thickness from Window <span className={subLabelClass}>(Min. 2 Images)</span></label>
                <PhotoCapture
                  onPhotosCaptured={(p) => handlePhotosCaptured('wallThicknessFromWindow', p)}
                  maxPhotos={999}
                  buttonLabel="Capture Wall Thickness from Window"
                  buttonClass={photoButtonClass}
                />
                {renderPhotoPreview(photos.wallThicknessFromWindow, 'wallThicknessFromWindow', 'Wall Thickness from Window')}
                {photos.wallThicknessFromWindow.length < 2 && (
                   <p className="text-xs text-red-500 mt-1">Minimum 2 images required.</p>
                )}
              </div>
            </div>
          </fieldset>

          <fieldset className="border border-gray-300 p-4 rounded-lg mt-4">
            <legend className="text-md font-semibold text-gray-700 px-2">Floor Construction</legend>
            <div className="space-y-4">
              <div className={fieldWrapperClass}>
                <label htmlFor="floorConstructionType" className={labelClass}>{requiredMarker}Floor construction type</label>
                <select
                  id="floorConstructionType"
                  name="floorConstructionType"
                  value={formData.floorConstructionType}
                  onChange={handleInputChange}
                  className={commonSelectClassName}
                  required
                >
                  {floorConstructionOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </select>
              </div>

              <div className={photoCaptureContainerClass}>
                <label className={labelClass}>Floor Construction Evidence</label>
                <PhotoCapture
                  onPhotosCaptured={(p) => handlePhotosCaptured('floorConstructionEvidenceImages', p)}
                  maxPhotos={999}
                  buttonLabel="Capture Floor Construction Images"
                  buttonClass={photoButtonClass}
                />
                {renderPhotoPreview(photos.floorConstructionEvidenceImages, 'floorConstructionEvidenceImages', 'Floor Construction')}
              </div>
              
              <div className={fieldWrapperClass}>
                <label className={labelClass}>{requiredMarker}Is there adequate ventilation to the sub-floor?</label>
                <div className="flex space-x-4 mt-2">
                  <button type="button" onClick={() => handleBooleanChange('adequateSubFloorVentilation', true)} className={`flex-1 py-3 rounded-lg border-2 ${formData.adequateSubFloorVentilation === true ? 'bg-yellow-100 border-yellow-400 text-yellow-700' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'} transition-colors font-medium`}>Yes</button>
                  <button type="button" onClick={() => handleBooleanChange('adequateSubFloorVentilation', false)} className={`flex-1 py-3 rounded-lg border-2 ${formData.adequateSubFloorVentilation === false ? 'bg-yellow-100 border-yellow-400 text-yellow-700' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'} transition-colors font-medium`}>No</button>
                </div>
              </div>
              
              <div className={fieldWrapperClass}>
                <label className={labelClass}>{requiredMarker}Is there any evidence of dampness to the floor?</label>
                <div className="flex space-x-4 mt-2">
                  <button type="button" onClick={() => handleBooleanChange('evidenceOfFloorDampness', true)} className={`flex-1 py-3 rounded-lg border-2 ${formData.evidenceOfFloorDampness === true ? 'bg-yellow-100 border-yellow-400 text-yellow-700' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'} transition-colors font-medium`}>Yes</button>
                  <button type="button" onClick={() => handleBooleanChange('evidenceOfFloorDampness', false)} className={`flex-1 py-3 rounded-lg border-2 ${formData.evidenceOfFloorDampness === false ? 'bg-yellow-100 border-yellow-400 text-yellow-700' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'} transition-colors font-medium`}>No</button>
                </div>
              </div>

              <div className={fieldWrapperClass}>
                <label className={labelClass}>{requiredMarker}Is there DPC Present?</label>
                <div className="flex space-x-4 mt-2">
                  <button type="button" onClick={() => handleBooleanChange('dpcPresent', true)} className={`flex-1 py-3 rounded-lg border-2 ${formData.dpcPresent === true ? 'bg-yellow-100 border-yellow-400 text-yellow-700' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'} transition-colors font-medium`}>Yes</button>
                  <button type="button" onClick={() => handleBooleanChange('dpcPresent', false)} className={`flex-1 py-3 rounded-lg border-2 ${formData.dpcPresent === false ? 'bg-yellow-100 border-yellow-400 text-yellow-700' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'} transition-colors font-medium`}>No</button>
                </div>
              </div>

              <div className={fieldWrapperClass}>
                <label className={labelClass}>{requiredMarker}Is the DPC visible above ground level?</label>
                <div className="flex space-x-4 mt-2">
                  <button type="button" onClick={() => handleBooleanChange('dpcVisible', true)} className={`flex-1 py-3 rounded-lg border-2 ${formData.dpcVisible === true ? 'bg-yellow-100 border-yellow-400 text-yellow-700' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'} transition-colors font-medium`}>Yes</button>
                  <button type="button" onClick={() => handleBooleanChange('dpcVisible', false)} className={`flex-1 py-3 rounded-lg border-2 ${formData.dpcVisible === false ? 'bg-yellow-100 border-yellow-400 text-yellow-700' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'} transition-colors font-medium`}>No</button>
                </div>
              </div>

              {formData.dpcPresent === true && (
                <div className={photoCaptureContainerClass}>
                  <label className={labelClass}>DPC Evidence Photos</label>
                  <PhotoCapture
                    onPhotosCaptured={(p) => handlePhotosCaptured('dpcEvidenceImages', p)}
                    maxPhotos={999}
                    buttonLabel="Capture DPC Evidence Photos"
                    buttonClass={photoButtonClass}
                  />
                  {renderPhotoPreview(photos.dpcEvidenceImages, 'dpcEvidenceImages', 'DPC Evidence')}
                </div>
              )}
            </div>
          </fieldset>

          <fieldset className="border border-gray-300 p-4 rounded-lg mt-4">
            <legend className="text-md font-semibold text-gray-700 px-2">Additional Notes</legend>
            <div className="space-y-4">
              <div className={fieldWrapperClass}>
                <label htmlFor="additionalNotes" className={labelClass}>Additional Notes for Property Front</label>
                <textarea
                  id="additionalNotes"
                  name="additionalNotes"
                  value={formData.additionalNotes}
                  onChange={handleInputChange}
                  rows={3}
                  placeholder="Enter any other relevant details here..."
                  className={commonSelectClassName}
                />
              </div>
            </div>
          </fieldset>

          {/* Custom Fields Section */}
          {customFields && customFields.length > 0 && (() => {
            // Group custom fields by section
            const fieldsBySection: Record<string, FieldSchema[]> = {};
            const fieldsWithoutSection: FieldSchema[] = [];
            
            customFields.forEach(field => {
              if (field.section) {
                if (!fieldsBySection[field.section]) {
                  fieldsBySection[field.section] = [];
                }
                fieldsBySection[field.section].push(field);
              } else {
                fieldsWithoutSection.push(field);
              }
            });
            
            // Sort fields within each section by sectionOrder
            Object.keys(fieldsBySection).forEach(sectionName => {
              fieldsBySection[sectionName].sort((a, b) => (a.sectionOrder || 0) - (b.sectionOrder || 0));
            });
            
            // Sort unsectioned fields by their order property
            fieldsWithoutSection.sort((a, b) => (a.order || 0) - (b.order || 0));
            
            // Sort sections by sectionOrder if provided
            let sectionEntries = Object.entries(fieldsBySection);
            if (sectionOrder && sectionOrder.length > 0) {
              sectionEntries.sort((a, b) => {
                const aIndex = sectionOrder.indexOf(a[0]);
                const bIndex = sectionOrder.indexOf(b[0]);
                
                if (aIndex !== -1 && bIndex !== -1) {
                  return aIndex - bIndex;
                }
                if (aIndex !== -1) return -1;
                if (bIndex !== -1) return 1;
                
                return a[0].localeCompare(b[0]);
              });
            } else {
              sectionEntries.sort((a, b) => a[0].localeCompare(b[0]));
            }
            
            return (
              <>
                {/* Render sectioned custom fields (excluding hidden sections) */}
                {sectionEntries
                  .filter(([sectionName]) => !hiddenSections.includes(sectionName))
                  .map(([sectionName, fields]) => (
                    <fieldset key={`custom_section_${sectionName}`} className="border border-gray-300 p-4 rounded-lg mt-4">
                      <legend className="text-md font-semibold text-gray-700 px-2">{sectionName}</legend>
                      <div className="space-y-4">
                        {fields.map(field => (
                          <DynamicFieldRenderer
                            key={field.id}
                            field={field}
                            value={customFieldData[field.id]}
                            onChange={(value) => handleCustomFieldChange(field.id, value)}
                            formData={customFieldData}
                            onFormDataChange={handleCustomFieldChange}
                          />
                        ))}
                      </div>
                    </fieldset>
                  ))}
                
                {/* Render unsectioned custom fields */}
                {fieldsWithoutSection.length > 0 && (
                  <fieldset className="border border-gray-300 p-4 rounded-lg mt-4">
                    <legend className="text-md font-semibold text-gray-700 px-2">Custom Fields</legend>
                    <div className="space-y-4">
                      {fieldsWithoutSection.map(field => (
                        <DynamicFieldRenderer
                          key={field.id}
                          field={field}
                          value={customFieldData[field.id]}
                          onChange={(value) => handleCustomFieldChange(field.id, value)}
                          formData={customFieldData}
                          onFormDataChange={handleCustomFieldChange}
                        />
                      ))}
                    </div>
                  </fieldset>
                )}
              </>
            );
          })()}
        </div>

        <div className="mt-8 pt-6 border-t border-gray-200 flex flex-col sm:flex-row gap-3 sm:justify-between">
          <button
            type="button"
            onClick={handleNavigateBack}
            className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 font-semibold rounded-lg shadow-sm hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2 transition-all duration-200 transform hover:scale-[1.02]"
          >
            <ArrowLeft size={18} /> Previous
          </button>
          <button
            type="submit"
            className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-semibold rounded-lg shadow-md hover:from-blue-600 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 transition-all duration-200 transform hover:scale-[1.02]"
          >
            Next <ArrowRight size={18} />
          </button>
        </div>
      </form>
      
      {/* Photo Gallery Modal */}
      {selectedGallery && (
        <PhotoGalleryModal
          isOpen={!!selectedGallery}
          onClose={handleCloseGallery}
          photos={(Array.isArray(photos[selectedGallery as keyof typeof photos]) 
            ? photos[selectedGallery as keyof typeof photos] 
            : []
          ).map((photo: DisplayablePhotoData) => ({
            id: photo.displayId,
            url: photo.url || photo.dataUrl, // Use CDN URL if available, fallback to dataUrl
            localPath: photo.url || photo.dataUrl,
          }))}
          sectionTitle={selectedGallery.replace(/([A-Z])/g, ' $1').trim()}
          onDeletePhoto={(photoId) => removePhoto(selectedGallery as keyof typeof photos, photoId)}
          onUpdateNote={() => {}}
          onGalleryStateChange={onGalleryStateChange}
        />
      )}

      {/* Compass Measurer Modal */}
      {showCompass && (
        <CompassMeasurer
          onDirectionCaptured={(direction, screenshotDataUrl) => {
            console.log('[PropertyFrontForm] Direction captured:', direction, 'Screenshot:', !!screenshotDataUrl);
            
            // Update form data with captured direction
            setFormData(prev => ({ ...prev, buildingOrientation: direction }));
            
            // Add screenshot to orientation photos if available and QUEUE FOR UPLOAD
            if (screenshotDataUrl) {
              const timestamp = Date.now();
              const displayId = `compass-measurement-${timestamp}`;
              const screenshotPhoto: DisplayablePhotoData = {
                displayId,
                dataUrl: screenshotDataUrl,
                base64String: screenshotDataUrl.split(',')[1] || screenshotDataUrl,
                fileName: `compass-direction-${timestamp}.png`,
                format: 'png',
                uploading: true // Mark as uploading
              };
              
              // Add to photos state
              setPhotos(prev => ({
                ...prev,
                orientationImage: [...prev.orientationImage, screenshotPhoto]
              }));
              
              console.log('[PropertyFrontForm] 🎯 Compass screenshot captured, queueing for CDN upload...');
              
              // Queue for upload to CDN (CRITICAL: prevents base64 bloat)
              queuePhotoUpload(
                displayId,
                screenshotDataUrl,
                'property_front',
                'orientationImage',
                // On success: replace base64 with CDN URL
                async (url: string) => {
                  setPhotos(current => ({
                    ...current,
                    orientationImage: current.orientationImage.map(p =>
                      p.displayId === displayId
                        ? { 
                            displayId, 
                            format: 'png', 
                            url, 
                            uploading: false,
                            dataUrl: '',  // Empty after upload
                            base64String: ''  // Empty after upload
                          } as DisplayablePhotoData
                        : p
                    )
                  }));
                  
                  // Save to DB with CDN URL
                  if (onDataUpdate) {
                    setTimeout(() => {
                      setPhotos(currentPhotos => {
                        console.log('[PropertyFrontForm] 💾 Saving compass screenshot with CDN URL');
                        onDataUpdate(prevData => ({
                          ...prevData,
                          property_front: {
                            ...prevData.property_front,
                            ...formData,
                            orientationImage: currentPhotos.orientationImage
                          }
                        }), 'property_front');
                        return currentPhotos;
                      });
                    }, 0);
                  }
                  
                  console.log('[PropertyFrontForm] ✅ Compass screenshot uploaded to CDN:', url);
                },
                // On error: keep base64 as fallback
                (error: string) => {
                  console.error('[PropertyFrontForm] ⚠️ Compass screenshot upload failed:', error);
                  setPhotos(current => ({
                    ...current,
                    orientationImage: current.orientationImage.map(p =>
                      p.displayId === displayId
                        ? { ...p, uploading: false, error }
                        : p
                    )
                  }));
                }
              );
            }
            
            setShowCompass(false);
          }}
          onClose={() => setShowCompass(false)}
        />
      )}
    </div>
  );
};

export default PropertyFrontForm; 